/**
 * The Codex export wrapper (briefs/phase-0/p0-7.md deliverable 3). Merges
 * amendments 2.1's wrapper with amendments 2.2's additions (settings,
 * currencies, per-actor warnings) and supersedes appendix A §8's `codex`/`env`
 * shape. `derived`'s field list follows appendix A §8's macro snapshot
 * exactly, since neither amendment changed its insides, only the envelope
 * around it.
 *
 * `packages/foundry-macro` imports nothing from this module (it has no
 * imports at all, so it can be pasted into Foundry): the two packages agree
 * on the shape only through the committed macro-output fixtures that both
 * `wrapper.test.ts` and `foundry-macro/src/core.test.ts` read.
 */
import { z } from '../schema/zod.js';
// ---------------------------------------------------------------------------
// `derived`: every key present and nullable, because each read in the macro
// is wrapped in try/null. Unknown keys are kept (never stripped) and flagged
// with the warning `derived_unknown_key`.
const AbilityDerived = z.object({
    value: z.number().nullable(),
    mod: z.number().nullable(),
    save: z.number().nullable(),
    dc: z.number().nullable(),
});
const SkillDerived = z.object({
    total: z.number().nullable(),
    passive: z.number().nullable(),
    mod: z.number().nullable(),
    bonus: z.number().nullable(),
    profMultiplier: z.number().nullable(),
});
const ToolDerived = z.object({ total: z.number().nullable() });
const AcDerived = z.object({
    value: z.number().nullable(),
    calc: z.string().nullable(),
    base: z.number().nullable(),
    armor: z.number().nullable(),
    dex: z.number().nullable(),
    shield: z.number().nullable(),
    bonus: z.number().nullable(),
    cover: z.number().nullable(),
    equippedArmor: z.string().nullable(),
    equippedShield: z.string().nullable(),
});
const HpDerived = z.object({
    value: z.number().nullable(),
    max: z.number().nullable(),
    effectiveMax: z.number().nullable(),
    temp: z.number().nullable(),
    tempmax: z.number().nullable(),
});
const InitDerived = z.object({ mod: z.number().nullable(), total: z.number().nullable() });
const SpellDerived = z.object({
    ability: z.string().nullable(),
    dc: z.number().nullable(),
    attack: z.number().nullable(),
    mod: z.number().nullable(),
});
const ClassDerived = z.object({
    identifier: z.string().nullable(),
    levels: z.number().nullable(),
    spellcasting: z
        .object({
        ability: z.string().nullable(),
        attack: z.number().nullable(),
        save: z.number().nullable(),
        preparationMax: z.number().nullable(),
    })
        .nullable(),
    hd: z
        .object({
        value: z.number().nullable(),
        max: z.number().nullable(),
        denomination: z.string().nullable(),
    })
        .nullable(),
});
const SpellSlotDerived = z.object({
    value: z.number().nullable(),
    max: z.number().nullable(),
    level: z.number().nullable(),
    override: z.number().nullable(),
});
const EncumbranceDerived = z.object({
    value: z.number().nullable(),
    max: z.number().nullable(),
    pct: z.number().nullable(),
    encumbered: z.boolean().nullable(),
});
const AttunementDerived = z.object({ value: z.number().nullable(), max: z.number().nullable() });
const AppliedEffectDerived = z.object({
    id: z.string().nullable(),
    name: z.string().nullable(),
    origin: z.string().nullable(),
    keys: z.array(z.string()).nullable(),
});
const ItemDerived = z.object({
    id: z.string().nullable(),
    uses: z.object({ value: z.number().nullable(), max: z.number().nullable() }).nullable(),
    // dnd5e 5.x stores a spell's `system.prepared` as 0/1/2, never a boolean.
    prepared: z.number().nullable(),
    equipped: z.boolean().nullable(),
    attuned: z.boolean().nullable(),
    labels: z.record(z.string(), z.unknown()).nullable(),
});
const DamagePartDerived = z.object({
    formula: z.string().nullable(),
    damageType: z.string().nullable(),
});
const ActivityDerived = z.object({
    id: z.string().nullable(),
    type: z.string().nullable(),
    labels: z
        .object({
        toHit: z.string().nullable(),
        damage: z.array(DamagePartDerived).nullable(),
        save: z.string().nullable(),
        healing: z.string().nullable(),
    })
        .nullable(),
    saveDC: z.number().nullable(),
    checkDC: z.number().nullable(),
    uses: z.object({ value: z.number().nullable(), max: z.number().nullable() }).nullable(),
});
const DerivedShape = {
    level: z.number().nullable(),
    prof: z.number().nullable(),
    // An NPC's `details.xp` carries only `value` (no `min`/`max`); every field
    // is independently nullable, not just the object as a whole.
    xp: z
        .object({
        value: z.number().nullable(),
        min: z.number().nullable(),
        max: z.number().nullable(),
    })
        .nullable(),
    abilities: z.record(z.string(), AbilityDerived.nullable()).nullable(),
    skills: z.record(z.string(), SkillDerived.nullable()).nullable(),
    tools: z.record(z.string(), ToolDerived.nullable()).nullable(),
    ac: AcDerived.nullable(),
    hp: HpDerived.nullable(),
    init: InitDerived.nullable(),
    movement: z.record(z.string(), z.number().nullable()).nullable(),
    senses: z.record(z.string(), z.number().nullable()).nullable(),
    spell: SpellDerived.nullable(),
    classes: z.array(ClassDerived).nullable(),
    spells: z.record(z.string(), SpellSlotDerived.nullable()).nullable(),
    encumbrance: EncumbranceDerived.nullable(),
    attunement: AttunementDerived.nullable(),
    scale: z.unknown().nullable(),
    appliedEffects: z.array(AppliedEffectDerived).nullable(),
    items: z.array(ItemDerived).nullable(),
    activities: z.array(ActivityDerived).nullable(),
};
export const KNOWN_DERIVED_KEYS = new Set(Object.keys(DerivedShape));
/** `z.looseObject`: unknown keys are kept, never stripped, so they can be flagged. */
export const DerivedSnapshotSchema = z.looseObject(DerivedShape);
// ---------------------------------------------------------------------------
// The wrapper envelope
const ActorEntrySchema = z.object({
    uuid: z.string(),
    source: z.record(z.string(), z.unknown()),
    derived: DerivedSnapshotSchema,
    warnings: z.array(z.string()),
});
const ModuleEntrySchema = z.object({ id: z.string(), version: z.string() });
const CurrencyEntrySchema = z.object({
    label: z.string(),
    abbreviation: z.string(),
    conversion: z.number(),
});
const SettingsSchema = z.object({
    encumbrance: z.string(),
    currencyWeight: z.boolean(),
});
const BaseWrapperSchema = z.object({
    format: z.literal('codex-foundry-export'),
    formatVersion: z.number(),
    exportedAt: z.iso.datetime(),
    world: z.object({ id: z.string(), title: z.string() }),
    core: z.string(),
    system: z.object({
        id: z.string(),
        version: z.string(),
        rulesVersion: z.enum(['legacy', 'modern']).nullable(),
    }),
    modules: z.array(ModuleEntrySchema),
    settings: SettingsSchema,
    currencies: z.record(z.string(), CurrencyEntrySchema),
    window: z.object({ from: z.iso.datetime(), to: z.iso.datetime() }).optional(),
    actors: z.array(ActorEntrySchema).optional(),
    messages: z.array(z.record(z.string(), z.unknown())).optional(),
});
export const CodexFoundryExportV1 = BaseWrapperSchema;
function isRecord(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
/**
 * Never throws. Refuses with a named reason (`newer_format_version`,
 * `bad_payload`, `window_without_messages`) before falling back to a generic
 * `schema:<path>` failure; on success, an unknown `derived` key is kept and
 * the actor's `warnings` gains `derived_unknown_key`.
 */
export function parseCodexExport(json) {
    if (!isRecord(json))
        return { ok: false, reasons: ['schema:root'] };
    const formatVersion = json.formatVersion;
    if (typeof formatVersion === 'number' && formatVersion > 1) {
        return { ok: false, reasons: ['newer_format_version'] };
    }
    const hasActors = Object.hasOwn(json, 'actors');
    const hasMessages = Object.hasOwn(json, 'messages');
    if (hasActors === hasMessages)
        return { ok: false, reasons: ['bad_payload'] };
    if (hasActors && Object.hasOwn(json, 'window'))
        return { ok: false, reasons: ['window_without_messages'] };
    const result = CodexFoundryExportV1.safeParse(json);
    if (!result.success) {
        const reasons = result.error.issues.map((issue) => `schema:${issue.path.join('.')}`);
        return { ok: false, reasons: reasons.length > 0 ? reasons : ['schema'] };
    }
    const value = { ...result.data };
    if (value.actors) {
        value.actors = value.actors.map((actor) => {
            const unknownKeys = Object.keys(actor.derived).filter((k) => !KNOWN_DERIVED_KEYS.has(k));
            if (unknownKeys.length === 0)
                return actor;
            const warnings = actor.warnings.includes('derived_unknown_key')
                ? actor.warnings
                : [...actor.warnings, 'derived_unknown_key'];
            return { ...actor, warnings };
        });
    }
    return { ok: true, value };
}
//# sourceMappingURL=wrapper.js.map